/**
 * GA4 client_id / user_id helpers for F1.5–F1.6.
 * Spec: wiki 07-analityka/instrukcja-gtm.md.
 */

import {
  ANALYTICS_COLLECTION_ENABLED,
  ensureGtagStubForIdentity,
  readStoredConsent,
} from './analyticsConfig';

/**
 * Parse GA4 client_id from the `_ga` cookie.
 * Cookie shape: `GA1.1.<client_id>` or `GA1.2.<client_id>` where client_id is `XXXXXXXXXX.YYYYYYYYYY`.
 */
export function parseGaClientIdFromCookie(cookieString = typeof document !== 'undefined' ? document.cookie : ''): string | null {
  if (!cookieString) return null;
  const match = cookieString.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match?.[1]) return null;
  const value = decodeURIComponent(match[1].trim());
  const parts = value.split('.');
  // GA1.<domainDepth>.<random>.<timestamp>
  if (parts.length < 4) return null;
  if (!parts[0].startsWith('GA')) return null;
  const clientId = `${parts[2]}.${parts[3]}`;
  if (!/^\d+\.\d+$/.test(clientId)) return null;
  return clientId;
}

export function getGaClientId(): string | null {
  if (typeof document === 'undefined') return null;
  return parseGaClientIdFromCookie(document.cookie);
}

/** True when the user granted Statystyka (analytics_storage). */
export function hasAnalyticsConsent(): boolean {
  return Boolean(readStoredConsent()?.analytics);
}

/**
 * client_id for `POST /subscriptions/checkout/` (BE Measurement Protocol).
 * Omit when Statystyka is denied — BE then skips GA4 `purchase`.
 */
export function getCheckoutClientId(): string | null {
  if (!ANALYTICS_COLLECTION_ENABLED) return null;
  if (!hasAnalyticsConsent()) return null;
  return getGaClientId();
}

/** Set GA4 user_id for subsequent hits (internal id only — never email). */
export function setGaUserId(userId: string | number | null | undefined): void {
  if (typeof window === 'undefined') return;
  if (!ANALYTICS_COLLECTION_ENABLED) return;

  ensureGtagStubForIdentity();
  const gtag = window.gtag;
  if (!gtag) return;

  if (userId === null || userId === undefined || userId === '') {
    gtag('set', { user_id: null });
    return;
  }

  gtag('set', { user_id: String(userId) });
}

export function clearGaUserId(): void {
  setGaUserId(null);
}
